/* OsCatalogGridViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./OsCatalogGridViewRenderer");
const Registrations = require("../../../../../RegistrationNamespaces");
const DateComponent = require("./column_renderers/date/DateComponent");

const PagingCatalogFeed = require("./PagingOsCatalogFeed");

const _ = require("lodash");

class OsCatalogGridViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        steveUtils,
        OsImageCatalog,
        userSession,
        storageFactory,
        solutionsManager,
        modalUserFeedback,
        userService
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._steveUtils = steveUtils;
        this._userSession = userSession;
        this._activeOrgId = this._userSession.getActiveOrganization().id;
        this._modalUserFeedback = modalUserFeedback;
        this._solutionsManager = solutionsManager;
        this._userService = userService;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "osCatalogExplorer";
        this._storageFactory = storageFactory;
        this._userGridData = undefined;
        this._osCatalog = OsImageCatalog;
        this._pagingCatalogFeed = undefined;
        this._renderer = new Renderer(this);
        this._explorerState = {
            selectedIds: [],
        };
        this._userInfo = undefined;
        this.columns = undefined;
        this._sessionStorage = undefined;
    }

    get columnConfig() {
        if (this.columns) {
            return this.columns;
        }
        this.columns = {
            name: {
                label: "Name",
                priority: 1,
            },
            description: {
                label: "Description",
                priority: 2,
            },
            imageCount: {
                label: "Images",
                width: 120,
                priority: 3,
                validation: {
                    min: 0,
                },
            },
            createdBy: {
                label: "Created By",
                priority: 4,
                width: 120,
                updateFromEntity: true,
            },
            createdOn: {
                label: "Created On",
                priority: 5,
                width: 120,
                columnRendererComponent: DateComponent,
                updateFromEntity: true,
            },
            updatedBy: {
                label: "Updated By",
                priority: 6,
                width: 120,
            },
            updatedOn: {
                label: "Updated On",
                priority: 7,
                width: 120,
                columnRendererComponent: DateComponent,
            },
        };

        return this.columns;
    }

    get pagingFeed() {
        return this._pagingCatalogFeed;
    }

    get selectedRows() {
        return this._renderer.selectedRows;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._solutionsManager.on(
            "activeSolutionChanged",
            (this._onSolutionChanged = this._onSolutionChanged.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._solutionsManager) {
            this._solutionsManager.removeListener("activeSolutionChanged", this._onSolutionChanged);
        }
    }

    _patchSorting(sorting) {
        // add second sorting order by name
        if (
            sorting &&
            Object.keys(sorting).length === 1 &&
            !sorting.name &&
            this.columnConfig.name
        ) {
            sorting.name = 1;
        }
    }

    _registerFeedListeners() {}

    _unregisterFeedListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        return Promise.all([this._storageFactory.whenStorageIsReady(), this._getMyUser()])
            .then(() => {
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );
                this._sessionStorage = this._storageFactory.getSessionStorageInstance(
                    "entity_grid_gl",
                    `${this._gridKey}_explorerState`
                );
                this._explorerState = Object.assign(
                    {},
                    this._explorerState,
                    this._sessionStorage.getData()
                );
            })
            .then(() => {
                this._pagingCatalogFeed = new PagingCatalogFeed({
                    feedKey: this._gridKey + "_feed",
                    userSession: this._userSession,
                    entityDef: this._osCatalog,
                    columnConfig: this.columnConfig,
                    filter:
                        _.get(this._userGridData.getBasicSearchData(), "filterQuery", {}) ||
                        this._userGridData.getAdvancedSearchData(),
                    sort: this._userGridData.getSorting(),
                });

                this._registerListeners();
                return Promise.resolve();
            })
            .then(() => {
                return this.whenFeedIsReady();
            });
    }

    wasFeedPreviouslyInitialized() {
        return this._pagingCatalogFeed.wasPreviouslyInitialized();
    }

    whenFeedIsReady() {
        return this._pagingCatalogFeed.whenFeedReady();
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    shutdown() {
        this._unregisterFeedListeners();
        this._unregisterListeners();
        let shutdowns = [
            _.invoke(this._renderer, "shutdown") || Promise.resolve(),
            _.invoke(this._pagingCatalogFeed, "shutdown") || Promise.resolve(),
            _.invoke(this._sessionStorage, "shutdown") || Promise.resolve(),
        ];
        return Promise.all(shutdowns).then(
            () => {
                //this._gameEditor._unregisterOsCatalogExplorerListeners(); // Unregister game editor events
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._steveUtils = undefined;
                this._explorerState = undefined;
                this._pagingCatalogFeed = undefined;
                this._userSession = undefined;
                this._modalUserFeedback = undefined;
                this._solutionsManager = undefined;
                this._solutionName = undefined;
                this._gridKey = undefined;
                this._userGridData = undefined;
                this._sessionStorage = undefined;
                this._logger = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "OsCatalogGridViewComponent.shutdown() error");
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get explorerState() {
        return this._explorerState;
    }

    set explorerState(val) {
        this.setExplorerState(val);
    }

    setExplorerState(val) {
        this._explorerState = val;
    }

    updateExplorerState(property, val) {
        this._sessionStorage.setData(val, property);
        _.set(this._explorerState, property, val);
    }

    resetSelection(newIds) {
        let selectedIds = [];

        if (newIds && newIds.length) {
            newIds.forEach((id) => {
                selectedIds.push(id);
            });
        }

        this.renderer._osCatalogEntityGrid.setSelectedRows(selectedIds);
        this.updateExplorerState("selectedIds", {selectedRows: selectedIds});
        this.renderer._renderAvailableActions();
    }

    loadAndScrollTo(entityId) {
        let that = this;
        this.pagingFeed.on("RequestedEntityFound", handleRequestedEntityFound);
        this.pagingFeed._requestEntity(entityId);

        function handleRequestedEntityFound(index) {
            that.renderer._osCatalogEntityGrid.renderer.scrollToTheRow(index);
            that.pagingFeed.removeListener("RefreshView", handleRequestedEntityFound);
        }
    }

    areCatalogsEnabled() {
        return this._osCatalog.areCatalogsEnabled(this._activeOrgId);
    }

    isUserCanCreateCatalog() {
        // return this._osCatalog.isUserAuthorizedForCreate(this._activeOrgId, this._userInfo);
        if (this._userSession.user.authorizedActions.indexOf("osmCatalogWrite") >= 0) {
            return Promise.resolve(true);
        }
        return Promise.resolve(false);
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    onUserSorted(sortData) {
        let sort = {};

        const sortCriteria =
            this.columnConfig[sortData.sortColumn].valuePath ||
            this.columnConfig[sortData.sortColumn].path ||
            sortData.sortColumn;

        sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        this._patchSorting(sort);

        this._pagingCatalogFeed.setSort(sort);
    }

    _onSolutionChanged(solution) {
        // whole pane will be reset, no need to make any action here
        this._solutionName = solution.name;
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    _getMyUser() {
        return this._userService.logMeInBro({}).then(
            (myInfo) => {
                this._userInfo = myInfo;
            },
            (err) => {
                console.log("I am unable to request your user info.", err);
            }
        );
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    openOsCatalogInstanceEditor(ids, fullyQualifiedViewName) {
        ids = [].concat(ids);

        const id = ids[0];
        let Catalog = this._pagingCatalogFeed.getOsCatalogEntityById(id);
        this._gameEditor.launchOsCatalogEditor(
            {
                fqn: fullyQualifiedViewName || Registrations.OS_CATALOG_EDITOR_VIEW_DETAILS,
            },
            {
                id: id,
                title: _.get(Catalog, "name", ""),
            }
        );
    }

    getSelectedCatalogIds() {
        return _.get(this._explorerState, "selectedIds", []);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsCatalogGridViewComponent;

OsCatalogGridViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.utils",
    "$teve.entities.OsImageCatalog",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
    "$teve.services.instance.User",
];

OsCatalogGridViewComponent.$displayDetails = {
    type: "View",
    name: "",
    icon: "osmanager.os_catalog",
    shown: false,
    order: 2,
};
